/**
 * Enterprise AI Microservice API Client
 *
 * Uses the unified API client. Nginx routes:
 *   /copilot/api/v1/*            → saf3ai-enterpriseai-microservice (Copilot)
 *   /gemini-enterprise/api/v1/*  → saf3ai-gemini-enterprise-microservice
 *   /copilot-studio/api/v1/*     → saf3ai-copilot-studio-microservice
 */

import api from './apiClient';
import { COPILOT_SCOPE_MODULES, copilotScopeParams } from '../copilot/copilotScopeStore';

// ---------------------------------------------------------------------------
// Shared: Conversation drift analysis
// ---------------------------------------------------------------------------
// Platform-agnostic — the AI microservice analyzes whatever session context
// is posted (user prompt + AI chunked responses) and caches the result by
// session_id. Reused by the Copilot, Gemini Enterprise, and Copilot Studio
// clients so every agent platform can surface the same "Saf3AI Conversation
// Analysis" card. Pass ``{ force_regenerate: true }`` as opts to bypass the
// cache when the user clicks the refresh button.
async function analyzeConversationDrift(payload, opts = {}) {
  const params = opts.force_regenerate ? { force_regenerate: 'true' } : {};
  const response = await api.post(
    '/ai-explorer/api/v1/agent/copilot-conversation-drift',
    payload,
    { params },
  );
  return response.data;
}

// ---------------------------------------------------------------------------
// Copilot agent / user scope transport
// ---------------------------------------------------------------------------
// The M365 Copilot and Copilot Studio services narrow every list, KPI and
// aggregate by `scope_agent` / `scope_user`. The module layout keeps the
// selection in copilotScopeStore while it is mounted; these wrappers append it
// to requests for their own prefix only (the Gemini Enterprise client below and
// the shared drift endpoint use the plain `api`). Paths the services answer
// without narrowing — health, config, posture (findings carry no app or user),
// the picker feed itself and per-item detail lookups — go out without it.
const UNSCOPED_PATHS = [
  /^\/health$/,
  /^\/config(\/|$)/,
  /^\/posture(\/|$)/,
  /^\/filters$/,
  /^\/users\/[^/]+$/,
  /^\/sessions\/[^/]+$/,
];

function withScope(module, prefix, url, config = {}) {
  if (typeof url !== 'string' || !url.startsWith(`${prefix}/`)) return config;
  const path = url.slice(prefix.length).split('?')[0];
  if (UNSCOPED_PATHS.some((re) => re.test(path))) return config;
  const scope = copilotScopeParams(module);
  if (!Object.keys(scope).length) return config;
  return { ...config, params: { ...(config.params || {}), ...scope } };
}

/** An `api` look-alike that appends the module's scope to its own prefix's requests. */
export function scopedClient(module, prefix) {
  return {
    get: (url, config) => api.get(url, withScope(module, prefix, url, config)),
    delete: (url, config) => api.delete(url, withScope(module, prefix, url, config)),
    post: (url, data, config) => api.post(url, data, withScope(module, prefix, url, config)),
    put: (url, data, config) => api.put(url, data, withScope(module, prefix, url, config)),
  };
}

// ---------------------------------------------------------------------------
// Copilot (Enterprise AI) — /copilot/api/v1
// ---------------------------------------------------------------------------

const COPILOT_PREFIX = '/copilot/api/v1';
const copilotHttp = scopedClient(COPILOT_SCOPE_MODULES.M365, COPILOT_PREFIX);

/**
 * Enterprise AI API endpoints
 */
export const enterpriseAIAPI = {
  // Health check
  async getHealth() {
    const response = await copilotHttp.get(`${COPILOT_PREFIX}/health`);
    return response.data;
  },

  // Dashboard
  async getDashboard(params = {}) {
    const response = await copilotHttp.get(`${COPILOT_PREFIX}/dashboard`, { params });
    return response.data;
  },

  // Tokenomics — token volume; cost fields are null (M365 Copilot is seat-licensed)
  async getTokenomics(params = {}) {
    const response = await copilotHttp.get(`${COPILOT_PREFIX}/tokenomics`, { params });
    return response.data;
  },

  // Users
  async getUsers(params = {}) {
    const response = await copilotHttp.get(`${COPILOT_PREFIX}/users`, { params });
    return response.data;
  },

  async getUserDetails(userEmail) {
    const response = await copilotHttp.get(`${COPILOT_PREFIX}/users/${encodeURIComponent(userEmail)}`);
    return response.data;
  },

  // Applications
  async getApplications(params = {}) {
    const response = await copilotHttp.get(`${COPILOT_PREFIX}/applications`, { params });
    return response.data;
  },

  // Sessions
  async getSessions(params = {}) {
    const response = await copilotHttp.get(`${COPILOT_PREFIX}/sessions`, { params });
    return response.data;
  },

  // Agent / user picker feed: raw `{ agents, users }` (gen_ai.application values
  // and emails) active in the window. Ignores the scope, so it is sent without it.
  async getFilters(params = {}) {
    const response = await copilotHttp.get(`${COPILOT_PREFIX}/filters`, { params });
    return response.data;
  },

  async getSessionDetails(sessionId) {
    const response = await copilotHttp.get(`${COPILOT_PREFIX}/sessions/${encodeURIComponent(sessionId)}`);
    return response.data;
  },

  // Sources — documents Copilot accessed for grounding
  async getSources(params = {}) {
    const response = await copilotHttp.get(`${COPILOT_PREFIX}/sources`, { params });
    return response.data;
  },

  // Recommendations — rule-based suggestions for the dashboard / users page
  async getRecommendations(params = {}) {
    const response = await copilotHttp.get(`${COPILOT_PREFIX}/recommendations`, { params });
    return response.data;
  },

  // Posture — metrics + findings written by CopilotM365PostureAnalyzer
  async getPostureMetrics() {
    const response = await copilotHttp.get(`${COPILOT_PREFIX}/posture/metrics`);
    return response.data;
  },
  async getPostureFindings(params = {}) {
    const response = await copilotHttp.get(`${COPILOT_PREFIX}/posture/findings`, { params });
    return response.data;
  },

  // Conversation drift analysis — see shared analyzeConversationDrift above.
  analyzeConversationDrift,

  // Threats
  async getThreats(params = {}) {
    const response = await copilotHttp.get(`${COPILOT_PREFIX}/threats`, { params });
    return response.data;
  },

  // Interactions
  async getInteractions(params = {}) {
    const response = await copilotHttp.get(`${COPILOT_PREFIX}/interactions`, { params });
    return response.data;
  },

  // Intent Analysis
  async getIntentAnalysis(params = {}) {
    const response = await copilotHttp.get(`${COPILOT_PREFIX}/intent-analysis`, { params });
    return response.data;
  },

  async getIntentCategories() {
    const response = await copilotHttp.get(`${COPILOT_PREFIX}/intent-analysis/categories`);
    return response.data;
  },

  // Knowledge Gaps
  async getKnowledgeGaps(params = {}) {
    const response = await copilotHttp.get(`${COPILOT_PREFIX}/knowledge-gaps`, { params });
    return response.data;
  },

  // Compliance
  async getCompliance(params = {}) {
    const response = await copilotHttp.get(`${COPILOT_PREFIX}/compliance`, { params });
    return response.data;
  },

  async getComplianceFrameworks() {
    const response = await copilotHttp.get(`${COPILOT_PREFIX}/compliance/frameworks`);
    return response.data;
  },

  async getComplianceViolations(params = {}) {
    const response = await copilotHttp.get(`${COPILOT_PREFIX}/compliance/violations`, { params });
    return response.data;
  },

  // Audit Logs
  async getAuditLogs(params = {}) {
    const response = await copilotHttp.get(`${COPILOT_PREFIX}/audit-log`, { params });
    return response.data;
  },

  async getAuditLogFilters() {
    const response = await copilotHttp.get(`${COPILOT_PREFIX}/audit-log/filters`);
    return response.data;
  },

  async exportAuditLogs(params = {}) {
    const response = await copilotHttp.get(`${COPILOT_PREFIX}/audit-log/export`, { params });
    return response.data;
  },

  // Configuration
  async getConfig(organizationId) {
    const response = await copilotHttp.get(`${COPILOT_PREFIX}/config`, {
      params: { organization_id: organizationId }
    });
    return response.data;
  },

  async updateConfig(organizationId, configData) {
    const response = await copilotHttp.put(`${COPILOT_PREFIX}/config`, configData, {
      params: { organization_id: organizationId }
    });
    return response.data;
  },

  async resetConfig(organizationId) {
    const response = await copilotHttp.post(`${COPILOT_PREFIX}/config/reset`, null, {
      params: { organization_id: organizationId }
    });
    return response.data;
  }
};

export default enterpriseAIAPI;


// =============================================================================
// Gemini Enterprise (Agents) Microservice — /gemini-enterprise/api/v1
// =============================================================================

const GE_PREFIX = '/gemini-enterprise/api/v1';

// ---------------------------------------------------------------------------
// In-flight GET dedup for the hot dashboard/config endpoints
// ---------------------------------------------------------------------------
// The Gemini dashboard page fans out one service call per card per load
// (getDashboardStats, getThreatStats, getUsageROI, getCompliance, getConfig),
// and several of those services call the SAME underlying endpoints. Without
// dedup the browser fires /dashboard and /config 3x per page load (GE-016).
//
// Keyed on the URL only: concurrent callers vary only cosmetic params
// (include_trends, organization_id) within a load and share the response
// shape. The entry lives only for the lifetime of the in-flight promise, so
// distinct loads (or a later date-range change) still fetch fresh data.
const inFlightGets = new Map();

function dedupeGet(url, params) {
  const key = `GET ${url}`;
  if (inFlightGets.has(key)) return inFlightGets.get(key);
  const promise = api
    .get(url, { params })
    .then((res) => res.data)
    .finally(() => inFlightGets.delete(key));
  inFlightGets.set(key, promise);
  return promise;
}

/**
 * Gemini Enterprise API endpoints (same contract as enterpriseAIAPI but different host)
 */
export const geminiEnterpriseAPI = {
  async getHealth() { return (await api.get(`${GE_PREFIX}/health`)).data; },
  async getDashboard(params = {}) { return dedupeGet(`${GE_PREFIX}/dashboard`, params); },
  async getUsers(params = {}) { return (await api.get(`${GE_PREFIX}/users`, { params })).data; },
  async getUserDetails(userEmail) { return (await api.get(`${GE_PREFIX}/users/${encodeURIComponent(userEmail)}`)).data; },
  async getApplications(params = {}) { return (await api.get(`${GE_PREFIX}/applications`, { params })).data; },
  async getUserAgentMapping(params = {}) { return (await api.get(`${GE_PREFIX}/topology/user-agent-mapping`, { params })).data; },
  async getSessions(params = {}) { return (await api.get(`${GE_PREFIX}/sessions`, { params })).data; },
  async getSessionFilters(params = {}) { return (await api.get(`${GE_PREFIX}/sessions/filters`, { params })).data; },
  async getSessionDetails(sessionId) { return (await api.get(`${GE_PREFIX}/sessions/${encodeURIComponent(sessionId)}`)).data; },
  async getThreats(params = {}) { return (await api.get(`${GE_PREFIX}/threats`, { params })).data; },
  async getInteractions(params = {}) { return (await api.get(`${GE_PREFIX}/interactions`, { params })).data; },
  async getIntentAnalysis(params = {}) { return (await api.get(`${GE_PREFIX}/intent-analysis`, { params })).data; },
  async getIntentCategories() { return (await api.get(`${GE_PREFIX}/intent-analysis/categories`)).data; },
  async getKnowledgeGaps(params = {}) { return (await api.get(`${GE_PREFIX}/knowledge-gaps`, { params })).data; },
  async getCompliance(params = {}) { return (await api.get(`${GE_PREFIX}/compliance`, { params })).data; },
  async getComplianceFrameworks() { return (await api.get(`${GE_PREFIX}/compliance/frameworks`)).data; },
  async getComplianceViolations(params = {}) { return (await api.get(`${GE_PREFIX}/compliance/violations`, { params })).data; },
  async getSources(params = {}) { return (await api.get(`${GE_PREFIX}/sources`, { params })).data; },
  async getAuditLogs(params = {}) { return (await api.get(`${GE_PREFIX}/audit-log`, { params })).data; },
  async getAuditLogFilters() { return (await api.get(`${GE_PREFIX}/audit-log/filters`)).data; },
  async exportAuditLogs(params = {}) { return (await api.get(`${GE_PREFIX}/audit-log/export`, { params })).data; },
  async getConfig(organizationId) { return dedupeGet(`${GE_PREFIX}/config`, { organization_id: organizationId }); },
  async updateConfig(organizationId, configData) { return (await api.put(`${GE_PREFIX}/config`, configData, { params: { organization_id: organizationId } })).data; },
  async resetConfig(organizationId) { return (await api.post(`${GE_PREFIX}/config/reset`, null, { params: { organization_id: organizationId } })).data; },
  // Posture Analysis
  async getPostureMetrics() { return (await api.get(`${GE_PREFIX}/posture/metrics`)).data; },
  async getPostureFindings(params = {}) { return (await api.get(`${GE_PREFIX}/posture/findings`, { params })).data; },
  async triggerPostureScan() { return (await api.post(`${GE_PREFIX}/posture/trigger`)).data; },
  async getDataAccessGraph(params = {}) { return (await api.get(`${GE_PREFIX}/posture/data-access`, { params })).data; },
  async getSourceSensitivity() { return (await api.get(`${GE_PREFIX}/sources/sensitivity`)).data; },
  async updateSourceSensitivity(data) { return (await api.put(`${GE_PREFIX}/sources/sensitivity`, data)).data; },
  async deleteSourceSensitivity(sourceId) { return (await api.delete(`${GE_PREFIX}/sources/sensitivity/${encodeURIComponent(sourceId)}`)).data; },
  // Grounding Analytics
  async getGroundingAnalytics(params = {}) { return (await api.get(`${GE_PREFIX}/grounding/analytics`, { params })).data; },
  // Feedback
  async getFeedbackSummary(params = {}) { return (await api.get(`${GE_PREFIX}/feedback/summary`, { params })).data; },
  // Usage & ROI
  async getUsageROI(params = {}) { return (await api.get(`${GE_PREFIX}/usage-roi`, { params })).data; },
  async getTokenomics(params = {}) { return (await api.get(`${GE_PREFIX}/tokenomics`, { params })).data; },
  // Recommendations
  async getRecommendations(params = {}) { return (await api.get(`${GE_PREFIX}/recommendations`, { params })).data; },
  // Adoption Analytics
  async getAdoptionMetrics(params = {}) { return (await api.get(`${GE_PREFIX}/adoption`, { params })).data; },
  // Gemini Enterprise customer policy
  async getPolicyRules(params = {}) { return (await api.get(`${GE_PREFIX}/policy/rules`, { params })).data; },
  async createPolicyRule(data) { return (await api.post(`${GE_PREFIX}/policy/rules`, data)).data; },
  async updatePolicyRule(ruleId, data) { return (await api.put(`${GE_PREFIX}/policy/rules/${encodeURIComponent(ruleId)}`, data)).data; },
  async deletePolicyRule(ruleId) { return (await api.delete(`${GE_PREFIX}/policy/rules/${encodeURIComponent(ruleId)}`)).data; },
  async syncPolicyRule(ruleId) { return (await api.post(`${GE_PREFIX}/policy/rules/${encodeURIComponent(ruleId)}/sync`)).data; },
  async getPolicyStatus() { return (await api.get(`${GE_PREFIX}/policy/status`)).data; },
  async simulatePolicyRule(payload) { return (await api.post(`${GE_PREFIX}/policy/simulate`, payload)).data; },
  async getPolicyViolations(params = {}) { return (await api.get(`${GE_PREFIX}/policy/violations`, { params })).data; },
  async getPolicyViolation(id, params = {}) { return (await api.get(`${GE_PREFIX}/policy/violations/${encodeURIComponent(id)}`, { params })).data; },
  async getPolicyViolationSummary(params = {}) { return (await api.get(`${GE_PREFIX}/policy/violations/summary`, { params })).data; },
  // Conversation drift analysis — shared platform-agnostic endpoint
  analyzeConversationDrift,
};


// =============================================================================
// Copilot Studio Microservice — /copilot-studio/api/v1
// =============================================================================

const CS_PREFIX = '/copilot-studio/api/v1';
const studioHttp = scopedClient(COPILOT_SCOPE_MODULES.STUDIO, CS_PREFIX);

/**
 * Copilot Studio API endpoints (same contract as geminiEnterpriseAPI)
 */
export const copilotStudioAPI = {
  async getHealth() { return (await studioHttp.get(`${CS_PREFIX}/health`)).data; },
  async getDashboard(params = {}) { return (await studioHttp.get(`${CS_PREFIX}/dashboard`, { params })).data; },
  async getTokenomics(params = {}) { return (await studioHttp.get(`${CS_PREFIX}/tokenomics`, { params })).data; },
  async getUsers(params = {}) { return (await studioHttp.get(`${CS_PREFIX}/users`, { params })).data; },
  async getUserDetails(userEmail) { return (await studioHttp.get(`${CS_PREFIX}/users/${encodeURIComponent(userEmail)}`)).data; },
  async getApplications(params = {}) { return (await studioHttp.get(`${CS_PREFIX}/applications`, { params })).data; },
  async getSessions(params = {}) { return (await studioHttp.get(`${CS_PREFIX}/sessions`, { params })).data; },
  // Agent / user picker feed: raw `{ agents, users }` (display names and emails); ignores the scope.
  async getFilters(params = {}) { return (await studioHttp.get(`${CS_PREFIX}/filters`, { params })).data; },
  async getSessionDetails(sessionId) { return (await studioHttp.get(`${CS_PREFIX}/sessions/${encodeURIComponent(sessionId)}`)).data; },
  async getThreats(params = {}) { return (await studioHttp.get(`${CS_PREFIX}/threats`, { params })).data; },
  async getInteractions(params = {}) { return (await studioHttp.get(`${CS_PREFIX}/interactions`, { params })).data; },
  async getIntentAnalysis(params = {}) { return (await studioHttp.get(`${CS_PREFIX}/intent-analysis`, { params })).data; },
  async getIntentCategories() { return (await studioHttp.get(`${CS_PREFIX}/intent-analysis/categories`)).data; },
  async getKnowledgeGaps(params = {}) { return (await studioHttp.get(`${CS_PREFIX}/knowledge-gaps`, { params })).data; },
  async getCompliance(params = {}) { return (await studioHttp.get(`${CS_PREFIX}/compliance`, { params })).data; },
  async getComplianceFrameworks() { return (await studioHttp.get(`${CS_PREFIX}/compliance/frameworks`)).data; },
  async getComplianceViolations(params = {}) { return (await studioHttp.get(`${CS_PREFIX}/compliance/violations`, { params })).data; },
  async getSources(params = {}) { return (await studioHttp.get(`${CS_PREFIX}/sources`, { params })).data; },
  async getAuditLogs(params = {}) { return (await studioHttp.get(`${CS_PREFIX}/audit-log`, { params })).data; },
  async getAuditLogFilters() { return (await studioHttp.get(`${CS_PREFIX}/audit-log/filters`)).data; },
  async exportAuditLogs(params = {}) { return (await studioHttp.get(`${CS_PREFIX}/audit-log/export`, { params })).data; },
  async getConfig(organizationId) { return (await studioHttp.get(`${CS_PREFIX}/config`, { params: { organization_id: organizationId } })).data; },
  async updateConfig(organizationId, configData) { return (await studioHttp.put(`${CS_PREFIX}/config`, configData, { params: { organization_id: organizationId } })).data; },
  async resetConfig(organizationId) { return (await studioHttp.post(`${CS_PREFIX}/config/reset`, null, { params: { organization_id: organizationId } })).data; },
  async getPostureMetrics() { return (await studioHttp.get(`${CS_PREFIX}/posture/metrics`)).data; },
  // Posture findings ride on /interactions (finding_type=posture), but posture spans carry no
  // agent or user, so the scope would only ever empty them: sent unscoped, like /posture/*.
  async getPostureInteractions(params = {}) { return (await api.get(`${CS_PREFIX}/interactions`, { params })).data; },
  async getAgentRegistry() { return (await studioHttp.get(`${CS_PREFIX}/agents/registry`)).data; },
  // Conversation drift analysis — shared platform-agnostic endpoint
  analyzeConversationDrift,
};
